import { memo, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import UpdateBanner from "src/components/UpdateBanner";
import usePathSegment from "src/hooks/usePathSegment";
import { menuTitleState } from "src/recoil/menuTitle/atom";
import useModalObserver from "src/recoil/modal-observer/hooks";
import { routeList } from "src/routes/routes";
import { isMobilePath, isPublicPage } from "src/utils/common-util";
import { GlobalNav } from "./GlobalNav";
import Layout from "./Layout";
import MetaTag from "./MetaTag";
import { SideBar } from "./SideBar";

/**
 * App > Container
 */

export const menuTitle = [
  {
    title: "대시보드",
    keyword: "main",
  },
  {
    title: "건물",
    keyword: "building",
  },
  {
    title: "공간 모니터링",
    keyword: "monitoring",
  },
  {
    title: "워크오더",
    keyword: "workorder",
  },
  {
    title: "VoC/고객문의",
    keyword: "question",
  },
  {
    title: "상품",
    keyword: "product",
  },
  {
    title: "신청/계약",
    keyword: "contract",
  },
  {
    title: "정산정보",
    keyword: "provider",
  },
  {
    title: "공용공간 예약",
    keyword: "reservation",
  },
  {
    title: "공용공간 정산",
    keyword: "common-space-settlement",
  },
  {
    title: "이용자",
    keyword: "user",
  },
  {
    title: "방문자",
    keyword: "visitor",
  },
  {
    title: "계약자",
    keyword: "org",
  },
  {
    title: "빌링 스케줄",
    keyword: "billing",
  },
  {
    title: "공용공간 요금",
    keyword: "price",
  },
  {
    title: "공용공간 휴무일시",
    keyword: "closedPeriod",
  },
  {
    title: "VoC 항목설정",
    keyword: "voc",
  },
  {
    title: "관리비 부과항목관리",
    keyword: "chargeItems",
  },
  {
    title: "관리비 부과관리",
    keyword: "chargeMnt",
  },
  {
    title: "공지사항",
    keyword: "notice",
  },
  {
    title: "이용안내",
    keyword: "guide",
  },
  {
    title: "CCTV 장치",
    keyword: "cctv",
  },
  {
    title: "출입장치",
    keyword: "access-device",
  },
  {
    title: "출입권한",
    keyword: "access-group",
  },
  {
    title: "출입기록 조회",
    keyword: "access-record",
  },
  {
    title: "예약정보",
    keyword: "reservationConfig",
  },
  {
    title: "IoT 관리",
    keyword: "iot-config",
  },
  {
    title: "계약그룹",
    keyword: "contract-group",
  },
  {
    title: "공간예약 신청",
    keyword: "contract-s2",
  },
  {
    title: "공간예약 결제",
    keyword: "billing-s2",
  },
  {
    title: "외부 링크 연결",
    keyword: "custom-link",
  },
  {
    title: "이용자 부가정보",
    keyword: "user-additional-info",
  },
  {
    title: "STPM 설정",
    keyword: "setting",
  },
];

export const isCleaningLayout = (pathname: string) => {
  const route = routeList.find((route) => {
    // :id, :name 등의 path parameter를 정규식 패턴으로 변환
    const pattern = route.path.replace(/:[^/]+/g, "[^/]+");
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });

  return route?.isCleaningLayout ?? false;
};

function Container() {
  const location = useLocation();
  const { pathname } = location;
  const setMenuTitle = useSetRecoilState(menuTitleState);
  const nowMenuURL = usePathSegment();
  const isInitPage = location.pathname.includes("init");
  const isErrorPage = location.pathname.includes("error");
  const isAccessDeniedPage = location.pathname.includes("accessdenied");

  const { notify, getLastId, modalObserver } = useModalObserver();

  useEffect(() => {
    const escapeKeyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const modalId = getLastId();
        if (modalId) {
          e.preventDefault();
          notify(modalId, e.key);
        }
      }
    };

    window.addEventListener("keyup", escapeKeyHandler);

    return () => {
      window.removeEventListener("keyup", escapeKeyHandler);
    };
  }, [modalObserver]);

  useEffect(() => {
    if (nowMenuURL) {
      const findNowMenu = menuTitle.find((el) => el.keyword === nowMenuURL);
      findNowMenu ? setMenuTitle(findNowMenu.title) : setMenuTitle("");
    } else {
      setMenuTitle("");
    }
  }, [isAccessDeniedPage, isInitPage, nowMenuURL, setMenuTitle]);

  return (
    <HelmetProvider>
      <>
        <MetaTag />
        <div
          className={
            isMobilePath(pathname)
              ? "mobile-override"
              : isInitPage
              ? "base-unfix mobile" // mobile 클래스는 '반응형까지 고려하여 pc-fix 최소너비를 unset으로 변경함'
              : !isPublicPage(pathname) && isErrorPage
              ? "base-pc-fix base-pc-error"
              : !isPublicPage(pathname)
              ? "base-pc-fix"
              : "public"
          }
        >
          {isPublicPage(pathname) === false &&
            isCleaningLayout(pathname) === false &&
            !isMobilePath(pathname) && ( //
              <GlobalNav
                logo={isInitPage ? isInitPage : isErrorPage ? isErrorPage : isAccessDeniedPage}
                className={isInitPage || isAccessDeniedPage || isErrorPage ? "no-sidebar-page" : ""}
              />
            )}
          {isPublicPage(pathname) === false &&
            isCleaningLayout(pathname) === false && //
            !pathname.includes("/mng/ctrl/init") &&
            !pathname.includes("/error/page-not-found") &&
            !pathname.includes("/mng/ctrl/accessdenied") && <SideBar />}
          <Layout />
          <UpdateBanner />
        </div>
      </>
    </HelmetProvider>
  );
}

export default memo(Container);
